import type { ReactNode } from "react";
import type { TenantRole } from "@fts/core";
import { useI18n, useT } from "../i18n";
import { STATUS_TONE, type VehicleStatus } from "../lib/fleet";
import { Icon, type IconName } from "./Icon";

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  help,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  /** A HelpTip shown after the title (outside the heading, so the heading's name stays the title). */
  help?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        {help ? (
          <div className="page-title-row">
            <h1 className="page-title">{title}</h1>
            {help}
          </div>
        ) : (
          <h1 className="page-title">{title}</h1>
        )}
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Panel({ title, meta, children, flush }: { title?: ReactNode; meta?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className={`panel${flush ? " panel-flush" : ""}`}>
      {(title || meta) && (
        <header className="panel-header">
          {title && <h2 className="panel-title">{title}</h2>}
          {meta && <div className="panel-meta">{meta}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function StatusBadge({ status }: { status: VehicleStatus }) {
  const t = useT();
  return (
    <span className={`badge tone-${STATUS_TONE[status]}`}>
      <span className={`status-dot tone-${status}`} aria-hidden="true" />
      {t(`status.${status}`)}
    </span>
  );
}

const ROLE_TONE: Record<TenantRole, string> = { admin: "accent", fleet_manager: "info", safety: "warn", viewer: "neutral" };

export function RoleBadge({ role }: { role: TenantRole }) {
  const t = useT();
  return <span className={`badge tone-${ROLE_TONE[role]}`}>{t(`role.${role}`)}</span>;
}

export function EmptyState({
  icon = "info",
  title,
  children,
  action,
  compact,
}: {
  icon?: IconName;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  /** Inside a panel or table box: less vertical padding. */
  compact?: boolean;
}) {
  return (
    <div className={`empty${compact ? " empty-compact" : ""}`}>
      <span className="empty-icon">
        <Icon name={icon} size={22} />
      </span>
      {title && <h2 className="empty-title">{title}</h2>}
      {children && <p className="empty-body">{children}</p>}
      {action}
    </div>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <p className="note">
      <Icon name="info" size={16} />
      <span>{children}</span>
    </p>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="mono">{children}</span>;
}

export type LoadingShape = "lines" | "table" | "cards" | "block";

/**
 * Loading placeholder: a skeleton in the shape of what is coming (table rows, KPI tiles, text lines or
 * a map/chart block). Screen readers hear "Yükleniyor…" once through the status role.
 */
export function Loading({ shape = "lines", rows }: { shape?: LoadingShape; rows?: number }) {
  const { t } = useI18n();
  const bar = (w: string, key?: number) => <span key={key} className="skeleton-bar" style={{ width: w }} />;
  let body: ReactNode;
  if (shape === "table") {
    const n = rows ?? 6;
    body = Array.from({ length: n + 1 }, (_, i) => (
      <div key={i} className="skeleton-row">
        {bar(i === 0 ? "60%" : "70%")}
        {bar(i === 0 ? "40%" : `${55 + ((i * 17) % 40)}%`)}
        {bar(i === 0 ? "50%" : "45%")}
        {bar(i === 0 ? "40%" : "60%")}
      </div>
    ));
  } else if (shape === "cards") {
    body = Array.from({ length: rows ?? 4 }, (_, i) => (
      <div key={i} className="skeleton-card">
        {bar("55%")}
        <span className="skeleton-bar" style={{ width: "40%", height: 28 }} />
        {bar("80%")}
      </div>
    ));
  } else if (shape === "block") {
    body = <span className="skeleton-bar skeleton-block" />;
  } else {
    body = Array.from({ length: rows ?? 3 }, (_, i) => bar(["90%", "75%", "60%", "82%"][i % 4]!, i));
  }
  return (
    <div className={`skeleton skeleton-${shape}`} role="status" aria-live="polite">
      <span className="sr-only">{t("common.loading")}</span>
      {body}
    </div>
  );
}
